/**
 * Д-3 — перенос и резервная копия устройства участника файлом `.totalchat`.
 *
 * Дизайн: `Задачи/Дизайн-Д-3-перенос-и-копия-переписки-участника-2026-09-02.md`.
 * Решения владельца 03.09 (§8): код переноса — СГЕНЕРИРОВАННЫЙ, 12 знаков;
 * транспорт — файл на устройстве человека (передача через сервер = v2).
 *
 * Почему переносится устройство ЦЕЛИКОМ, а не «история»: аккаунт Olm привязан
 * к did (имя стора его содержит), поэтому пара «стор + did» и есть устройство.
 * Сервер при этом не трогается вовсе — опыт §7а показал, что клиент после
 * возврата не написал ему ни одного запроса, did и строка `msg_devices`
 * остались прежними, а код безопасности у владельца совпал знак в знак.
 *
 * 🔴 ГЛАВНОЕ, что опыт §7а изменил в этом коде. Возврат СТАРОЙ копии без
 * сброса сессий НЕ ЛЕЧИТСЯ САМ: шесть попыток в каждую сторону и перезагрузка
 * обеих сторон не протолкнули ни одного сообщения — собеседник на каждую
 * попытку показывал «Сообщение не удалось расшифровать». Тот же сценарий со
 * сбросом стора `session` при импорте сошёлся с ПЕРВОЙ попытки и без потерь.
 * Отсюда [dropStaleSessions] и правило: `kind: 'backup'` импортируется только
 * со сброшенными сессиями. Цена размена измерена: сброс отнимает то
 * единственное сообщение из промежутка, которое ещё расшифровывалось.
 *
 * Модуль ЧИСТЫЙ: без сети, без роутера, без React. Внешние зависимости —
 * `crypto.subtle` и фабрика IndexedDB — принимаются параметрами, чтобы всё,
 * кроме самого похода в базу, проверялось тестом (IndexedDB в jsdom нет).
 */

/** Что лежит в начале файла открытым текстом — по нему узнаём свой формат. */
export const MAGIC = 'TOTALCHAT';
export const FORMAT_VERSION = 1;

/** transfer — плановый переезд (старое устройство гасим); backup — копия. */
export const KIND_TRANSFER = 'transfer';
export const KIND_BACKUP = 'backup';

/**
 * Алфавит кода переноса — Crockford base32: РОВНО 32 знака, поэтому 12 знаков
 * дают ровно 60 бит. Ни `I`, ни `L`, ни `O`, ни `U` в нём нет: первые три
 * путаются с единицей и нулём при переписывании с экрана на бумагу, `U`
 * выброшена, чтобы код не сложился в грубое слово.
 */
export const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const CODE_LENGTH = 12;
export const CODE_GROUP = 4;

/** PBKDF2 при 600 000 итераций — потолок, который iOS 15 проходит за ~1 с. */
export const PBKDF2_ITERATIONS = 600_000;
export const SALT_BYTES = 16;
export const NONCE_BYTES = 12;

/** Три ключа localStorage, в которых живёт «кто я» на этом устройстве. */
export const LS_SESSION = 'msg_member_session';
export const LS_DEVICE_ID = 'msg_device_id';
export const LS_STORE_PASS = 'msg_store_pass';
export const LS_KEYS = [LS_SESSION, LS_DEVICE_ID, LS_STORE_PASS];

/**
 * Что из них едет В ФАЙЛ. `msg_device_id` не едет: на импорте он не читается,
 * а в браузере с кабинетом это did владельца (ревью 04.09).
 */
export const FILE_LS_KEYS = [LS_SESSION, LS_STORE_PASS];

/** База истории — общая для всех страниц чата в этом браузере. */
export const HISTORY_DB = 'total-msg';

/**
 * Крипто-стор — это ДВЕ базы, а не одна (снято прогоном §7а; в §3 дизайна было
 * написано «стор», и файл с одной базой не поднял бы машину).
 * `…::matrix-sdk-crypto` держит аккаунт и сессии, `…-meta` — служебное.
 */
export const CRYPTO_SUFFIX = '::matrix-sdk-crypto';
export const CRYPTO_META_SUFFIX = '::matrix-sdk-crypto-meta';

/** Имя objectStore с цепочками Olm — тот самый, что сбрасывается у копии. */
export const SESSION_STORE = 'session';

export function cryptoDbNames(storeName) {
  return [`${storeName}${CRYPTO_SUFFIX}`, `${storeName}${CRYPTO_META_SUFFIX}`];
}

/* ------------------------------------------------------------------ код --- */

/**
 * Сгенерировать код переноса: 12 знаков тремя группами, `k7m4-p2x9-r5w8`.
 *
 * 🔴 Отбраковка, а не остаток от деления: `byte % 32` при алфавите из 32 знаков
 * ровный, но стоит алфавиту измениться — и остаток тихо перекосит распределение.
 * Берём только байты ниже кратной границы, остальные выбрасываем.
 */
export function generateTransferCode(getRandomValues = defaultRandom) {
  const out = [];
  const limit = 256 - (256 % CODE_ALPHABET.length);
  while (out.length < CODE_LENGTH) {
    const chunk = new Uint8Array(CODE_LENGTH);
    getRandomValues(chunk);
    for (const b of chunk) {
      if (b >= limit || out.length >= CODE_LENGTH) continue;
      out.push(CODE_ALPHABET[b % CODE_ALPHABET.length]);
    }
  }
  return formatTransferCode(out.join(''));
}

/** Разбить на группы по четыре и показать строчными — так его легче списать. */
export function formatTransferCode(raw) {
  const s = String(raw).toUpperCase();
  const groups = [];
  for (let i = 0; i < s.length; i += CODE_GROUP) groups.push(s.slice(i, i + CODE_GROUP));
  return groups.join('-').toLowerCase();
}

/**
 * Привести введённое человеком к канону.
 *
 * 🔴 Прощаем ровно то, что человек путает, списывая с экрана: регистр, дефисы
 * и пробелы, `O`→`0`, `I`/`L`→`1`. Не прощать это значит отдавать человеку
 * «неверный код» на верном коде — а второго экземпляра файла у него нет.
 */
export function normalizeTransferCode(text) {
  const up = String(text ?? '').toUpperCase().replace(/[\s-]/g, '');
  const fixed = up.replace(/O/g, '0').replace(/[IL]/g, '1');
  return fixed;
}

export function transferCodeIsValid(text) {
  const c = normalizeTransferCode(text);
  return c.length === CODE_LENGTH && [...c].every((ch) => CODE_ALPHABET.includes(ch));
}

/* ---------------------------------------------------------------- кодек --- */

/**
 * Тегированный кодек: значения крипто-стора двоичные (Uint8Array), а файл —
 * JSON. Без тегов `JSON.stringify(new Uint8Array([1,2]))` даёт `{"0":1,"1":2}`,
 * и на возврате стор поднялся бы с мусором вместо ключей — молча.
 *
 * Незнакомые типы НЕ проглатываются: [encodeValue] бросает. Тихая потеря
 * значения здесь означала бы файл, который импортируется без ошибки и не
 * работает — худший из возможных исходов.
 */
export function encodeValue(v) {
  if (v === null || v === undefined) return v;
  const t = typeof v;
  if (t === 'string' || t === 'number' || t === 'boolean') return v;
  if (v instanceof Uint8Array) return { __t: 'u8', b: bytesToBase64(v) };
  if (v instanceof ArrayBuffer) return { __t: 'ab', b: bytesToBase64(new Uint8Array(v)) };
  if (ArrayBuffer.isView(v)) {
    return {
      __t: 'tv',
      n: v.constructor.name,
      b: bytesToBase64(new Uint8Array(v.buffer, v.byteOffset, v.byteLength)),
    };
  }
  if (v instanceof Date) return { __t: 'date', v: v.toISOString() };
  if (v instanceof Map) {
    return { __t: 'map', e: [...v.entries()].map(([k, x]) => [encodeValue(k), encodeValue(x)]) };
  }
  if (v instanceof Set) return { __t: 'set', e: [...v.values()].map(encodeValue) };
  if (Array.isArray(v)) return { __t: 'arr', a: v.map(encodeValue) };
  if (t === 'object' && (v.constructor === Object || v.constructor === undefined)) {
    const o = {};
    for (const k of Object.keys(v)) o[k] = encodeValue(v[k]);
    return { __t: 'obj', o };
  }
  throw new Error(`deviceTransfer: не умею класть в файл значение типа ${
    v.constructor ? v.constructor.name : t}`);
}

export function decodeValue(v) {
  if (v === null || v === undefined) return v;
  if (typeof v !== 'object') return v;
  switch (v.__t) {
    case 'u8': return base64ToBytes(v.b);
    case 'ab': return base64ToBytes(v.b).buffer;
    case 'tv': {
      const raw = base64ToBytes(v.b);
      const Ctor = globalThis[v.n];
      if (typeof Ctor !== 'function') throw new Error(`неизвестный тип ${v.n}`);
      return new Ctor(raw.buffer, raw.byteOffset, raw.byteLength / Ctor.BYTES_PER_ELEMENT);
    }
    case 'date': return new Date(v.v);
    case 'map': return new Map(v.e.map(([k, x]) => [decodeValue(k), decodeValue(x)]));
    case 'set': return new Set(v.e.map(decodeValue));
    case 'arr': return v.a.map(decodeValue);
    case 'obj': {
      const o = {};
      for (const k of Object.keys(v.o)) o[k] = decodeValue(v.o[k]);
      return o;
    }
    default:
      throw new Error('файл повреждён: значение без тега');
  }
}

export function bytesToBase64(u8) {
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < u8.length; i += CHUNK) {
    s += String.fromCharCode.apply(null, u8.subarray(i, i + CHUNK));
  }
  return btoa(s);
}

export function base64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

/* --------------------------------------------------------------- отбор --- */

/**
 * Оставить в базе истории только записи ЭТОГО участника.
 *
 * 🔴 Зачем фильтр. `total-msg` одна на origin: если человек в этом же браузере
 * открывал кабинет владельца, там лежит и чужая переписка. Файл участника,
 * унёсший её, — утечка, которой никто не заметил бы: расшифровать он её не
 * сможет, но метаданные и тексты владельца в нём были бы.
 */
export function filterHistory(dump, { conversationId, roomIds = [] } = {}) {
  const rooms = new Set(roomIds);
  const db = dump.dbs.find((d) => d.name === HISTORY_DB);
  if (!db) return dump;
  const keep = (store) => {
    if (store.name === 'messages' || store.name === 'outbox') {
      return store.records.filter((r) => convOf(r) === conversationId);
    }
    if (store.name === 'roomMessages' || store.name === 'roomOutbox') {
      return store.records.filter((r) => rooms.has(roomOf(r)));
    }
    if (store.name === 'kv') return store.records.filter((r) => kvBelongs(r, conversationId, rooms));
    return store.records;
  };
  return {
    ...dump,
    dbs: dump.dbs.map((d) => (d.name !== HISTORY_DB
      ? d
      : { ...d, stores: d.stores.map((s) => ({ ...s, records: keep(s) })) })),
  };
}

/** Ключи kv у нас именованные: `last-seq:<conv>`, `peer-identity:<conv>`, `room-cursor:<room>`. */
function kvBelongs(record, conversationId, rooms) {
  const key = typeof record.k === 'string' ? record.k : null;
  if (key === null) return false;
  const at = key.indexOf(':');
  if (at < 0) return false;
  const name = key.slice(0, at);
  const tail = key.slice(at + 1);
  if (name === 'room-cursor') return rooms.has(tail);
  return tail === conversationId;
}

const plain = (r) => (r && r.v && r.v.__t === 'obj' ? r.v.o : null);
function convOf(record) {
  const o = plain(record);
  return o && typeof o.conversationId === 'string' ? o.conversationId : null;
}
function roomOf(record) {
  const o = plain(record);
  return o && typeof o.roomId === 'string' ? o.roomId : null;
}

/**
 * Выбросить устаревшие цепочки Olm — лекарство §6, доказанное опытом E2b.
 *
 * Трогается РОВНО стор `session` и только в базе `…::matrix-sdk-crypto`:
 * `core` (аккаунт), `devices`, `identities`, `tracked_users` обязаны остаться,
 * иначе поменяется identity — а с ней did, код безопасности у собеседника и
 * право на строку `msg_devices`.
 */
export function dropStaleSessions(dump) {
  let dropped = 0;
  const dbs = dump.dbs.map((d) => {
    if (!d.name.endsWith(CRYPTO_SUFFIX)) return d;
    return {
      ...d,
      stores: d.stores.map((s) => {
        if (s.name !== SESSION_STORE) return s;
        dropped += s.records.length;
        return { ...s, records: [] };
      }),
    };
  });
  return { dump: { ...dump, dbs }, dropped };
}

/* ---------------------------------------------------------------- файл --- */

/** Заголовок читается БЕЗ кода: человеку надо видеть, что это за файл. */
export function buildHeader({ kind, createdAt, displayName, salt, nonce }) {
  if (kind !== KIND_TRANSFER && kind !== KIND_BACKUP) {
    throw new Error(`неизвестный вид файла: ${kind}`);
  }
  return {
    magic: MAGIC,
    v: FORMAT_VERSION,
    kind,
    createdAt,
    displayName: displayName ?? null,
    salt: bytesToBase64(salt),
    nonce: bytesToBase64(nonce),
  };
}

/**
 * Разобрать файл и сказать человеческим языком, что с ним не так.
 *
 * 🔴 «Не тот файл» и «не тот код» — РАЗНЫЕ беды с разным лечением, и путать их
 * нельзя: на «не тот код» человек ищет бумажку, на «не тот файл» — другой файл.
 * Поэтому заголовок проверяется до расшифровки и своими сообщениями.
 */
export function parseFile(text) {
  let obj;
  try {
    obj = JSON.parse(text);
  } catch {
    throw new Error('Это не файл переписки Total — его не удалось прочитать.');
  }
  if (!obj || obj.magic !== MAGIC) {
    throw new Error('Это не файл переписки Total.');
  }
  if (obj.v !== FORMAT_VERSION) {
    throw new Error(`Файл сделан другой версией приложения (v${obj.v}). Обновите страницу.`);
  }
  if (obj.kind !== KIND_TRANSFER && obj.kind !== KIND_BACKUP) {
    throw new Error('Файл переписки повреждён: неизвестный вид.');
  }
  if (typeof obj.salt !== 'string' || typeof obj.nonce !== 'string'
      || typeof obj.body !== 'string') {
    throw new Error('Файл переписки повреждён: не хватает частей.');
  }
  return obj;
}

export function fileName(displayName, createdAt, kind) {
  const day = String(createdAt).slice(0, 10);
  const who = String(displayName ?? '')
    .trim().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '')
    .slice(0, 40) || 'chat';
  return `total-${kind === KIND_BACKUP ? 'copy' : 'transfer'}-${who}-${day}.totalchat`;
}

/* ------------------------------------------------------------- крипто ----- */

const defaultRandom = (buf) => globalThis.crypto.getRandomValues(buf);
const defaultSubtle = () => globalThis.crypto.subtle;

async function deriveKey(code, salt, subtle) {
  const material = await subtle.importKey(
    'raw', new TextEncoder().encode(normalizeTransferCode(code)),
    'PBKDF2', false, ['deriveKey'],
  );
  return subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'],
  );
}

/**
 * Собрать файл: заголовок открыт, тело зашифровано кодом переноса.
 *
 * 🔴 Заголовок идёт в AAD. Иначе `kind` можно было бы подменить в текстовом
 * редакторе — и копия месячной давности импортировалась бы как «перенос», то
 * есть БЕЗ сброса сессий, а это ровно тот случай, который опыт E2 показал
 * неработающим.
 */
export async function packFile(dump, code, {
  kind, displayName, createdAt = new Date().toISOString(),
  subtle = defaultSubtle(), getRandomValues = defaultRandom,
} = {}) {
  const salt = new Uint8Array(SALT_BYTES);
  const nonce = new Uint8Array(NONCE_BYTES);
  getRandomValues(salt);
  getRandomValues(nonce);
  const header = buildHeader({ kind, createdAt, displayName, salt, nonce });
  const key = await deriveKey(code, salt, subtle);
  const body = new TextEncoder().encode(JSON.stringify(dump));
  const aad = new TextEncoder().encode(JSON.stringify(header));
  const cipher = await subtle.encrypt(
    { name: 'AES-GCM', iv: nonce, additionalData: aad }, key, body,
  );
  return JSON.stringify({ ...header, body: bytesToBase64(new Uint8Array(cipher)) });
}

/** Разобрать и расшифровать. Ошибка кода — отдельным, узнаваемым текстом. */
export async function unpackFile(text, code, { subtle = defaultSubtle() } = {}) {
  const obj = parseFile(text);
  const { body, ...header } = obj;
  const key = await deriveKey(code, base64ToBytes(header.salt), subtle);
  const aad = new TextEncoder().encode(JSON.stringify(header));
  let clear;
  try {
    clear = await subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBytes(header.nonce), additionalData: aad },
      key, base64ToBytes(body),
    );
  } catch {
    throw new Error('Код не подошёл. Проверьте, что переписали его без ошибок.');
  }
  return { header, dump: JSON.parse(new TextDecoder().decode(clear)) };
}

/* ------------------------------------------------------- связка --------- */

/** Имя крипто-стора участника — контракт с `msgRuntime.storeNameFor`. */
export function memberStoreName(memberId, did) {
  if (!Number.isInteger(memberId) || memberId <= 0 || !did) {
    throw new Error(`не могу построить имя стора: ${memberId}/${did}`);
  }
  return `total-msg-m${memberId}-${did}`;
}

/**
 * Кто мы на этом устройстве — только то, из чего строится имя стора.
 *
 * 🔴 Отдельно от `readSession` намеренно (ревью 03.09, Low). Тот требует ещё и
 * `conversationId`, а `memberSession.valid()` считает его НЕобязательным:
 * сессии, снятые до среза комнат, обязаны остаться рабочими. Лечению эта
 * строгость не нужна и вредна — участник с такой сессией услышал бы «на этом
 * устройстве не осталось данных о вашей переписке», что было бы неправдой.
 */
function readDeviceIdentity(storage) {
  // 🔴 `code` — чтобы страница отличала «сессии нет» от «браузер не дал
  // хранилище» (ревью р.2, Low): первое лечится новой ссылкой, второе —
  // обычным окном вместо приватного. Один общий текст отправил бы человека
  // делать не то.
  const fail = (text) => Object.assign(new Error(text), { code: 'no_session' });
  const raw = storage.getItem(LS_SESSION);
  if (!raw) throw fail('На этом устройстве нет открытой переписки — переносить нечего.');
  const s = JSON.parse(raw);
  if (!s || !Number.isInteger(s.memberId) || !s.did) {
    throw fail('Данные переписки на этом устройстве неполные.');
  }
  return s;
}

function readSession(storage) {
  const raw = storage.getItem(LS_SESSION);
  if (!raw) throw new Error('На этом устройстве нет открытой переписки — переносить нечего.');
  const s = JSON.parse(raw);
  if (!s || !Number.isInteger(s.memberId) || !s.did || !s.conversationId) {
    throw new Error('Данные переписки на этом устройстве неполные.');
  }
  return s;
}

/**
 * Снять устройство целиком и запечатать в файл.
 *
 * 🔴 Порядок «история, потом стор» не косметика: между двумя снимками может
 * прийти сообщение, и тогда в стор попадёт сессия, продвинутая дальше, чем
 * история. Обратный порядок дал бы историю НОВЕЕ ключей — то есть строку на
 * экране, которую нечем расшифровать. Транспорт на время снимка глушит
 * вызывающий (§4), здесь мы только держим порядок.
 */
export async function exportDevice({
  kind = KIND_BACKUP, storage = localStorage, idb = globalThis.indexedDB,
  roomIds = [], read = readDatabases, subtle = defaultSubtle(),
  getRandomValues = defaultRandom, createdAt = new Date().toISOString(),
} = {}) {
  const session = readSession(storage);
  const ls = {};
  // 🔴 В файл едут ДВА ключа, а не три (ревью 04.09, Medium). `msg_device_id`
  // на импорте не читается вовсе (`applyTransferFile` берёт did из сессии), а
  // в браузере, где живёт кабинет, это did ВЛАДЕЛЬЦА — класть его в файл,
  // который открывается кодом из двенадцати знаков, незачем.
  // 🔴 `msg_store_pass` уехать ОБЯЗАН: без него машина не откроет стор,
  // который тут же лежит рядом. Остаточный риск (в браузере с кабинетом фраза
  // общая, значит в файле участника лежит и фраза стора владельца) снимается
  // только правильной починкой — ключом по принципалу; владелец 04.09 выбрал
  // пояс-отказ, риск записан в тикете.
  for (const k of FILE_LS_KEYS) ls[k] = storage.getItem(k);

  const storeName = memberStoreName(session.memberId, session.did);
  const history = await read([HISTORY_DB], idb);
  const crypto = await read(cryptoDbNames(storeName), idb);
  const dump = filterHistory(
    { ls, dbs: [...history.dbs, ...crypto.dbs] },
    { conversationId: session.conversationId, roomIds },
  );
  if (!dump.dbs.some((d) => d.name.endsWith(CRYPTO_SUFFIX))) {
    throw new Error('Ключи переписки на этом устройстве не найдены — переносить нечего.');
  }
  const code = generateTransferCode(getRandomValues);
  const text = await packFile(dump, code, {
    kind, displayName: session.displayName, createdAt, subtle, getRandomValues,
  });
  return { text, code, name: fileName(session.displayName, createdAt, kind) };
}

/**
 * Прочитать файл — БЕЗ единой записи в браузер.
 *
 * 🔴 Разделение на «прочитать» и «положить» — не стиль, а защита порядка из
 * §4: сперва сервер должен подтвердить, что доступ к переписке жив
 * (`POST /msg/members/me/token`), и только потом что-то пишется. Если бы обе
 * половины жили в одной функции, «сначала проверка» держалось бы на памяти
 * того, кто её зовёт. Здесь положить нечего, пока нет свежего токена.
 */
export async function readTransferFile(text, code, { subtle = defaultSubtle() } = {}) {
  const { header, dump } = await unpackFile(text, code, { subtle });
  const raw = dump?.ls?.[LS_SESSION];
  if (!raw) throw new Error('Файл переписки повреждён: в нём нет данных участника.');
  let session;
  try { session = JSON.parse(raw); } catch {
    throw new Error('Файл переписки повреждён: данные участника не читаются.');
  }
  if (!session || !Number.isInteger(session.memberId) || !session.did) {
    throw new Error('Файл переписки повреждён: в нём нет участника и устройства.');
  }
  const storeName = memberStoreName(session.memberId, session.did);
  if (!dump.dbs?.some((d) => d.name === `${storeName}${CRYPTO_SUFFIX}`)) {
    throw new Error('Файл переписки повреждён: ключи в нём не от этого участника.');
  }
  return { header, dump, session };
}

/* --------------------------------------------- пояс-отказ по паролю ----- */

/** В браузере живёт ЧУЖОЙ крипто-стор — его пароль заменять нельзя. */
export const CONFLICT_FOREIGN = 'foreign_store';
/** Перечислить базы нечем, а кабинет в этом браузере есть — значит рискуем. */
export const CONFLICT_UNKNOWN = 'cannot_check';

/**
 * Следы кабинета в этом браузере. Читаются ТОЛЬКО как признак «здесь работали
 * кабинетом» и только в запасном пути пояса: в браузерах без
 * `indexedDB.databases()` (Firefox до 126, Safari до 14) перечислить стор
 * владельца нечем, а рискнуть его перепиской нельзя. Значения не разбираются.
 *
 * 🔴 Одного `access_token` мало: выход из кабинета стирает ЕГО
 * (`modalContent/exit/exit.jsx:12`) и он же слетает на 401
 * (`utils/router/axiosInstance.jsx`), а крипто-стор владельца остаётся на
 * диске. Поэтому рядом стоят ключи экрана плана — их пишет только кабинет
 * (`Map`, `Aside`, `PlanEditor`, `Settings`), страницы участника не трогают
 * ни один, и выход их не убирает.
 */
export const LS_CABINET_KEYS = [
  'access_token', 'selectedBuildingId', 'selectedFloor', 'buildingName',
  'current_floor_amount',
];

/**
 * 🔴 Пояс-отказ (решение владельца 04.09).
 *
 * `msg_store_pass` — фраза, которой зашифрован крипто-стор, — ОДНА на весь
 * браузер: её читают и участник (`msgRuntime`), и кабинет владельца
 * (`chatController`), каждый своим стором. А импорт клал сюда значение ИЗ
 * ФАЙЛА. В браузере, где рядом открыт кабинет, это тихо ломало ЕГО: стор
 * владельца остался бы зашифрован прежней фразой, которой больше нет нигде.
 * Ни ошибки, ни подсказки — просто чат, который перестал открываться.
 *
 * Ровно то же правило записано строкой ниже для `msg_device_id` (ревью р.4):
 * ключ браузера не затирается значением из файла. Пароль под него не попал.
 *
 * Правильное лечение — свой ключ на каждого принципала с миграцией; владелец
 * 04.09 выбрал дешёвое: не чинить крипто, а ЗАПРЕТИТЬ перенос в браузер, где
 * уже живёт чужая переписка. Цена решения записана в тикете: в таком браузере
 * перенос и лечение недоступны вовсе.
 *
 * Отказываем, только когда замена действительно что-то сломает: своя фраза
 * есть, она ДРУГАЯ, и в браузере лежит крипто-стор не этого устройства.
 *
 * 🔴 Запасной путь узкий НАМЕРЕННО. Первая версия отказывала всякий раз, когда
 * базы не перечисляются, — и на этом покраснел живой пин лечения: в jsdom
 * `indexedDB` нет вовсе. В настоящем браузере без `databases()` (Firefox до
 * 126, Safari до 14) это означало бы, что лечение из копии не работает
 * НИКОГДА — участник, у которого погиб стор, остался бы ни с чем. Поэтому
 * «не знаю» превращается в отказ только там, где есть что ломать: если в
 * браузере лежит вход в кабинет.
 *
 * @returns {null|{reason: string, names: string[]}}
 */
export async function storePassConflict({ dump, session }, {
  storage = localStorage, idb = globalThis.indexedDB,
} = {}) {
  const filePass = dump?.ls?.[LS_STORE_PASS] ?? null;
  const mine = storage.getItem(LS_STORE_PASS);
  // Пустая фраза ломаться не умеет, совпадающая — не меняется.
  if (!mine || mine === filePass) return null;
  const blindFallback = () => (LS_CABINET_KEYS.some((k) => storage.getItem(k) !== null)
    ? { reason: CONFLICT_UNKNOWN, names: [] }
    : null);
  // Чей стор участника в этом браузере ещё жив. Битую сессию читаем как «нет»:
  // открыть по ней всё равно нечего.
  const liveMemberStore = (() => {
    try {
      const raw = storage.getItem(LS_SESSION);
      const s = raw ? JSON.parse(raw) : null;
      return Number.isInteger(s?.memberId) && s?.did
        ? memberStoreName(s.memberId, s.did) : null;
    } catch { return null; }
  })();
  let names;
  try {
    // 🔴 Отдельной охраны `typeof idb?.databases !== 'function'` здесь НЕТ
    // намеренно (ревью 04.09, Low): её ветка неотличима от `catch` — при
    // `idb: {}` вызов падает сюда же. Охрана, которую нельзя отличить пином,
    // это не защита, а обещание.
    names = (await idb.databases()).map((d) => d?.name).filter(Boolean);
  } catch {
    return blindFallback();
  }
  // 🔴 Своими считаем ОБЕ базы этого устройства: `-meta` не оканчивается на
  // `CRYPTO_SUFFIX`, поэтому в отбор не попадает и без того, но список должен
  // читаться как «наше», а не как хитрость суффиксов.
  const own = new Set(cryptoDbNames(memberStoreName(session.memberId, session.did)));
  const foreign = names.filter((n) => {
    if (!n.endsWith(CRYPTO_SUFFIX) || own.has(n)) return false;
    const store = n.slice(0, -CRYPTO_SUFFIX.length);
    // 🔴 Стор кабинета (`-o<id>-`, `-e<id>-`) живой ВСЕГДА: человек войдёт и
    // откроет его завтра. Стор УЧАСТНИКА — только пока цела его сессия
    // (ревью 04.09, High). «Выйти из чата» и «войти как другой участник»
    // сессию стирают, а крипто-базу оставляют на диске: считай мы её живой,
    // пояс запирал бы перенос в этом браузере навсегда — и говорил бы при
    // этом про «соседнюю переписку», которую продукт сам объявил недоступной
    // навсегда. На лендинге панель показана ровно под `!alreadyIn`, то есть
    // живого участника там нет по построению.
    if (!store.startsWith(`${HISTORY_DB}-m`)) return true;
    return liveMemberStore !== null && store === liveMemberStore;
  });
  return foreign.length ? { reason: CONFLICT_FOREIGN, names: foreign } : null;
}

/**
 * Положить прочитанное в браузер. `freshToken` обязателен по построению —
 * он бывает только из ответа сервера, значит проверка уже прошла.
 *
 * Копия (`kind: 'backup'`) кладётся ТОЛЬКО со сброшенными сессиями: опыт §7а
 * (E2 против E2b) показал, что иначе канал не сходится ни в одну сторону.
 *
 * 🔴 Пояс-отказ стоит ЗДЕСЬ, а не только в панели, и стоит ДО первой записи.
 * Панель показывает человеку честный экран, но если бы проверка жила только в
 * ней, порядок держался бы памятью того, кто позовёт `applyTransferFile`
 * следующим — тем же способом, каким чуть не потерялся `freshToken`.
 */
export async function applyTransferFile({ header, dump, session }, {
  freshToken, storage = localStorage, idb = globalThis.indexedDB, write = writeDatabases,
} = {}) {
  if (!freshToken) {
    throw new Error('applyTransferFile: без свежего токена писать нельзя — сперва проверка');
  }
  const clash = await storePassConflict({ dump, session }, { storage, idb });
  if (clash) {
    throw Object.assign(
      new Error('В этом браузере уже открыта другая переписка Total — перенос сюда'
        + ' заменил бы её пароль хранилища.'),
      { code: clash.reason },
    );
  }
  const stale = header.kind === KIND_BACKUP;
  const { dump: toWrite, dropped } = stale
    ? dropStaleSessions(dump)
    : { dump, dropped: 0 };

  const records = await write(toWrite, idb);
  storage.setItem(LS_SESSION, JSON.stringify({ ...session, token: freshToken }));
  // 🔴 `msg_device_id` — ключ БРАУЗЕРА, а не участника (ревью р.4, Medium).
  // Его читает кабинет владельца (`browserDeviceId()`), участник же живёт от
  // `session.did`. Клади мы сюда значение ИЗ ФАЙЛА — копия, снятая до того как
  // владелец нажал Д-20, вернула бы в браузер ревокнутый did, и кабинет в этом
  // же браузере лёг бы 403-м на prekeys без единой подсказки о причине.
  // Поэтому: не затираем чужое значение, а пустому даём did этой сессии.
  if (!storage.getItem(LS_DEVICE_ID)) storage.setItem(LS_DEVICE_ID, session.did);
  storage.setItem(LS_STORE_PASS, dump.ls[LS_STORE_PASS]);
  return { records, droppedSessions: dropped, kind: header.kind };
}

/**
 * «Закрыть чат здесь» — второй шаг переноса.
 *
 * 🔴 Без стирания старая копия рано или поздно отправит сообщение и разойдётся
 * с новой по рачету (§2): собеседник увидит «старый индекс» и перестанет
 * расшифровывать обе. Поэтому перенос ОБЯЗАН гасить исходное устройство, а
 * копия — не обязана.
 *
 * 🔴 Стирается переписка ЭТОГО участника, а не база `total-msg` целиком: в том
 * же браузере может жить кабинет владельца, и снести его историю заодно —
 * потеря, которой человек не просил.
 */
export async function wipeDevice({
  storage = localStorage, idb = globalThis.indexedDB, roomIds = [],
  dropDb = deleteDatabase, forget = forgetHistoryOf,
} = {}) {
  const session = readSession(storage);
  const storeName = memberStoreName(session.memberId, session.did);
  await forget({ conversationId: session.conversationId, roomIds }, idb);
  for (const name of cryptoDbNames(storeName)) await dropDb(name, idb);
  // 🔴 Стираем ОДНУ сессию, а не три ключа (ревью р.5, Medium). Два других —
  // `msg_device_id` и `msg_store_pass` — принадлежат БРАУЗЕРУ, а не участнику:
  // did читает кабинет владельца, а паролем зашифрован в том числе его стор.
  // Снося их «заодно», мы делали ровно то, от чего docstring выше защищает
  // историю: кабинет в этом же браузере после «Закрыть чат здесь» заводил
  // новый did, терял расшифрованную переписку, ловил вопрос Д-20 и зажигал
  // «ключ сменился» у всех собеседников — без единого объяснения.
  // Терять тут нечего: крипто-стор участника уже снесён, а пароль без стора
  // не открывает ничего.
  storage.removeItem(LS_SESSION);
  return { storeName };
}

/**
 * Д-3 «хвост»: снести ТОЛЬКО крипто-стор — перед восстановлением из копии.
 *
 * Отличие от `wipeDevice` принципиальное: переписку (`total-msg`) и ключи
 * localStorage не трогаем. Стор к этому моменту уже мёртв (его ключи сервер
 * не принимает, 409), а история — цела, и копия может быть старше её: снести
 * историю заодно значило бы потерять то, что копия не вернёт.
 *
 * 🔴 Звать ТОЛЬКО при остановленном чате. Живая машина держит соединение с
 * базой, и `deleteDatabase` при нём упирается в `blocked` — молча, без
 * ошибки (живой прогон E3). Порядок держит страница: сперва `flushSync` +
 * `waitLocalHandoff`, и лишь потом сюда.
 */
export async function dropCryptoStore({
  storage = localStorage, idb = globalThis.indexedDB, dropDb = deleteDatabase,
} = {}) {
  const device = readDeviceIdentity(storage);
  const storeName = memberStoreName(device.memberId, device.did);
  // 🔴 Исход удаления возвращаем НАРУЖУ (ревью 03.09, Medium). `deleteDatabase`
  // по построению не бросает: и `onerror`, и `onblocked` дают false. Проглоти
  // мы этот false — файл лёг бы ПОВЕРХ живого стора: `writeDatabases` кладёт
  // `put` в существующие objectStore, а сброс сессий чистит только дамп, и на
  // диске старые сессии уцелели бы. Это в точности тупик E2, причём немой:
  // экран сказал бы, что всё восстановлено.
  let dropped = true;
  for (const name of cryptoDbNames(storeName)) {
    if (!await dropDb(name, idb)) dropped = false;
  }
  return { storeName, dropped };
}

export function deleteDatabase(name, idb = globalThis.indexedDB) {
  return new Promise((resolve) => {
    const req = idb.deleteDatabase(name);
    // `blocked` — не ошибка: соседняя вкладка держит соединение. Разрешаем и
    // идём дальше, иначе «Закрыть чат здесь» повиснет молча навсегда.
    req.onsuccess = () => resolve(true);
    req.onerror = () => resolve(false);
    req.onblocked = () => resolve(false);
  });
}

/** Выкинуть из `total-msg` записи одного участника, не трогая чужие. */
export async function forgetHistoryOf({ conversationId, roomIds = [] }, idb = globalThis.indexedDB) {
  const rooms = new Set(roomIds);
  let db;
  try { db = await asPromise(idb.open(HISTORY_DB)); } catch { return 0; }
  const names = Array.from(db.objectStoreNames);
  let removed = 0;
  for (const name of names) {
    const os = db.transaction(name, 'readonly').objectStore(name);
    const values = await asPromise(os.getAll());
    const keys = await asPromise(os.getAllKeys());
    const doomed = keys.filter((key, i) => {
      const v = values[i];
      if (name === 'messages' || name === 'outbox') return v?.conversationId === conversationId;
      if (name === 'roomMessages' || name === 'roomOutbox') return rooms.has(v?.roomId);
      if (name === 'kv') return kvBelongs({ k: key }, conversationId, rooms);
      return false;
    });
    if (!doomed.length) continue;
    removed += await new Promise((resolve, reject) => {
      const tx = db.transaction(name, 'readwrite');
      const store = tx.objectStore(name);
      for (const key of doomed) store.delete(key);
      tx.oncomplete = () => resolve(doomed.length);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }
  db.close();
  return removed;
}

/* ------------------------------------------------------- IndexedDB ------- */

const asPromise = (req) => new Promise((resolve, reject) => {
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

/** Снять базы целиком: схема (её надо будет воссоздать) и записи. */
export async function readDatabases(names, idb = globalThis.indexedDB) {
  const dbs = [];
  for (const name of names) {
    const open = idb.open(name);
    // Базы может не быть вовсе (комнату не открывали) — это не ошибка.
    let db;
    try { db = await asPromise(open); } catch { continue; }
    if (!db.objectStoreNames.length) { db.close(); continue; }
    const stores = [];
    for (const sName of Array.from(db.objectStoreNames)) {
      const os = db.transaction(sName, 'readonly').objectStore(sName);
      const values = await asPromise(os.getAll());
      const keys = await asPromise(os.getAllKeys());
      stores.push({
        name: sName,
        keyPath: os.keyPath === undefined ? null : os.keyPath,
        autoIncrement: os.autoIncrement,
        indexes: Array.from(os.indexNames).map((n) => {
          const ix = os.index(n);
          return {
            name: n, keyPath: ix.keyPath, unique: ix.unique, multiEntry: ix.multiEntry,
          };
        }),
        records: values.map((v, i) => ({ k: encodeValue(keys[i]), v: encodeValue(v) })),
      });
    }
    dbs.push({ name: db.name, version: db.version, stores });
    db.close();
  }
  return { dbs };
}

/**
 * Положить базы обратно, воссоздав схему ровно ту, что снята.
 *
 * 🔴 Класть НАДО до подъёма приложения. Если открыть чат с пустым стором,
 * машина заведёт НОВЫЙ аккаунт Olm под тем же did — сервер ответит 409, и
 * перенос станет невозможен уже навсегда (did ревокнуть нельзя переиграть).
 */
export async function writeDatabases(dump, idb = globalThis.indexedDB) {
  let records = 0;
  for (const db of dump.dbs) {
    const open = idb.open(db.name, db.version);
    open.onupgradeneeded = () => {
      const handle = open.result;
      for (const s of db.stores) {
        const os = handle.createObjectStore(s.name, {
          keyPath: s.keyPath, autoIncrement: s.autoIncrement,
        });
        for (const ix of s.indexes) {
          os.createIndex(ix.name, ix.keyPath, { unique: ix.unique, multiEntry: ix.multiEntry });
        }
      }
    };
    const handle = await asPromise(open);
    for (const s of db.stores) {
      if (!s.records.length) continue;
      // Счётчик живёт ВНУТРИ обещания и возвращается им: замыкание в цикле,
      // мутирующее внешнюю переменную, — это не только придирка линтера, но и
      // способ посчитать записи транзакции, которая ещё не завершилась.
      const written = await new Promise((resolve, reject) => {
        const tx = handle.transaction(s.name, 'readwrite');
        const os = tx.objectStore(s.name);
        let n = 0;
        for (const rec of s.records) {
          if (s.keyPath === null) os.put(decodeValue(rec.v), decodeValue(rec.k));
          else os.put(decodeValue(rec.v));
          n += 1;
        }
        tx.oncomplete = () => resolve(n);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
      records += written;
    }
    handle.close();
  }
  return records;
}
